import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Calendar, Plus, Edit, Trash2, MapPin, User, Clock, Camera, MessageSquare, ChevronRight, Eye, Upload, Send } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const statusLabels = { planifie: "Planifié", en_cours: "En cours", termine: "Terminé", annule: "Annulé" };
const statusColors = {
  planifie: "bg-blue-50 text-blue-600", en_cours: "bg-amber-50 text-amber-600",
  termine: "bg-emerald-50 text-emerald-600", annule: "bg-slate-100 text-slate-500"
};

export default function InterventionsPage() {
  const { apiFetch, API, user } = useAuth();
  const isWebmaster = user?.role === "webmaster";
  const [interventions, setInterventions] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", date_debut: "", date_fin: "", location: "", assigned_to: "", commande_id: "", notes: "", status: "planifie" });
  const [commentText, setCommentText] = useState("");
  const [commentVisibility, setCommentVisibility] = useState("public");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    const [intRes, cmdRes] = await Promise.all([
      apiFetch(`${API}/interventions`),
      apiFetch(`${API}/commandes`)
    ]);
    if (intRes.ok) setInterventions(await intRes.json());
    if (cmdRes.ok) setCommandes(await cmdRes.json());
    setLoading(false);
  }, [apiFetch, API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditId(null);
    setForm({ title: "", description: "", date_debut: new Date().toISOString().slice(0, 10), date_fin: "", location: "", assigned_to: "", commande_id: "", notes: "", status: "planifie" });
    setDialogOpen(true);
  };

  const openEdit = (i) => {
    setEditId(i.intervention_id);
    setForm({ title: i.title, description: i.description || "", date_debut: i.date_debut?.slice(0, 10) || "", date_fin: i.date_fin?.slice(0, 10) || "", location: i.location || "", assigned_to: i.assigned_to || "", commande_id: i.commande_id || "", notes: i.notes || "", status: i.status || "planifie" });
    setDialogOpen(true);
  };

  const openDetail = async (i) => {
    const res = await apiFetch(`${API}/interventions/${i.intervention_id}`);
    if (res.ok) {
      setSelectedIntervention(await res.json());
      setDetailOpen(true);
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.date_debut) { toast.error("Titre et date de début requis"); return; }
    const url = editId ? `${API}/interventions/${editId}` : `${API}/interventions`;
    const method = editId ? "PUT" : "POST";
    const res = await apiFetch(url, { method, body: JSON.stringify(form) });
    if (res.ok) {
      toast.success(editId ? "Intervention mise à jour" : "Intervention créée");
      setDialogOpen(false);
      fetchData();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette intervention ?")) return;
    const res = await apiFetch(`${API}/interventions/${id}`, { method: "DELETE" });
    if (res.ok) { toast.success("Intervention supprimée"); fetchData(); }
  };

  const handleProgressUpdate = async (interventionId, progress) => {
    await apiFetch(`${API}/interventions/${interventionId}/progress`, {
      method: "PUT",
      body: JSON.stringify({ progress })
    });
    fetchData();
    if (selectedIntervention?.intervention_id === interventionId) {
      const res = await apiFetch(`${API}/interventions/${interventionId}`);
      if (res.ok) setSelectedIntervention(await res.json());
    }
  };

  const handleStatusChange = async (interventionId, newStatus) => {
    const res = await apiFetch(`${API}/interventions/${interventionId}`, {
      method: "PUT",
      body: JSON.stringify({ status: newStatus })
    });
    if (res.ok) {
      if (newStatus === "termine") {
        toast.success("Intervention terminée. Facture générée automatiquement.");
      }
      fetchData();
    }
  };

  const handlePhotoUpload = async (files) => {
    if (!selectedIntervention || files.length === 0) return;
    setUploading(true);
    const formData = new FormData();
    for (const f of files) formData.append("files", f);
    try {
      const res = await fetch(`${API}/interventions/${selectedIntervention.intervention_id}/photos`, {
        method: "POST",
        body: formData,
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        const refreshRes = await apiFetch(`${API}/interventions/${selectedIntervention.intervention_id}`);
        if (refreshRes.ok) setSelectedIntervention(await refreshRes.json());
        fetchData();
      }
    } catch (e) { toast.error("Erreur upload"); }
    finally { setUploading(false); }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !selectedIntervention) return;
    const res = await apiFetch(`${API}/interventions/${selectedIntervention.intervention_id}/comments`, {
      method: "POST",
      body: JSON.stringify({ text: commentText, visibility: commentVisibility })
    });
    if (res.ok) {
      toast.success("Commentaire ajouté");
      setCommentText("");
      const refreshRes = await apiFetch(`${API}/interventions/${selectedIntervention.intervention_id}`);
      if (refreshRes.ok) setSelectedIntervention(await refreshRes.json());
    }
  };

  const F = (field, value) => setForm(s => ({ ...s, [field]: value }));
  const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "-";

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = interventions.filter(i => i.date_debut >= today && i.status !== "annule" && i.status !== "termine");

  if (loading) return <div className="p-4 sm:p-8"><div className="animate-pulse h-96 bg-slate-200 rounded-lg" /></div>;

  return (
    <div className="p-4 sm:p-8" data-testid="interventions-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-barlow text-2xl font-bold text-slate-900 tracking-tight uppercase">Interventions</h1>
          <p className="text-sm text-slate-500 mt-1">Planifiez et suivez vos interventions terrain</p>
        </div>
        <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="create-intervention-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle Intervention
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: "Planifiées", count: interventions.filter(i => i.status === "planifie").length, color: "text-blue-600" },
          { label: "En cours", count: interventions.filter(i => i.status === "en_cours").length, color: "text-amber-600" },
          { label: "Terminées", count: interventions.filter(i => i.status === "termine").length, color: "text-emerald-600" },
          { label: "Total", count: interventions.length, color: "text-slate-900" },
        ].map(s => (
          <Card key={s.label} className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className={`text-2xl font-bold font-barlow ${s.color}`}>{s.count}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Upcoming cards */}
      {upcoming.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">A venir ({upcoming.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcoming.map(i => (
              <Card key={i.intervention_id} className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDetail(i)} data-testid={`intervention-card-${i.intervention_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-900">{i.title}</h3>
                    <Badge className={`${statusColors[i.status]} text-[10px] border-0`}>{statusLabels[i.status]}</Badge>
                  </div>
                  {i.description && <p className="text-xs text-slate-500 mb-2 line-clamp-2">{i.description}</p>}
                  {/* Progress bar */}
                  <div className="mb-2">
                    <div className="flex justify-between text-[10px] text-slate-400 mb-0.5">
                      <span>Progression</span>
                      <span>{i.progress || 0}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${i.progress || 0}%` }} />
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-slate-500">
                    <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {formatDate(i.date_debut)}{i.date_fin ? ` - ${formatDate(i.date_fin)}` : ""}</div>
                    {i.location && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {i.location}</div>}
                    {i.assigned_to && <div className="flex items-center gap-1.5"><User className="w-3 h-3" /> {i.assigned_to}</div>}
                    {i.commande_number && <div className="text-xs text-blue-500">Commande: {i.commande_number}</div>}
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-400">
                    {(i.photos?.length || 0) > 0 && <span className="flex items-center gap-0.5"><Camera className="w-3 h-3" /> {i.photos.length}</span>}
                    {(i.comments?.length || 0) > 0 && <span className="flex items-center gap-0.5"><MessageSquare className="w-3 h-3" /> {i.comments.length}</span>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* All interventions table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Toutes les interventions</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {interventions.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-400">Aucune intervention planifiée</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold">Titre</TableHead>
                  <TableHead className="text-xs font-semibold">Date</TableHead>
                  <TableHead className="text-xs font-semibold">Lieu</TableHead>
                  <TableHead className="text-xs font-semibold">Assigné</TableHead>
                  <TableHead className="text-xs font-semibold">Commande</TableHead>
                  <TableHead className="text-xs font-semibold">Progression</TableHead>
                  <TableHead className="text-xs font-semibold">Statut</TableHead>
                  <TableHead className="text-xs font-semibold w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {interventions.map(i => (
                  <TableRow key={i.intervention_id} data-testid={`intervention-row-${i.intervention_id}`}>
                    <TableCell className="text-sm font-medium">{i.title}</TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(i.date_debut)}</TableCell>
                    <TableCell className="text-sm text-slate-500">{i.location || "-"}</TableCell>
                    <TableCell className="text-sm text-slate-500">{i.assigned_to || "-"}</TableCell>
                    <TableCell className="text-sm text-slate-500">{i.commande_number || "-"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${i.progress || 0}%` }} />
                        </div>
                        <span className="text-xs text-slate-400">{i.progress || 0}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select value={i.status} onValueChange={(v) => handleStatusChange(i.intervention_id, v)}>
                        <SelectTrigger className="h-7 text-xs w-28 border-0">
                          <Badge className={`${statusColors[i.status]} text-[10px] border-0`}>{statusLabels[i.status]}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="planifie">Planifié</SelectItem>
                          <SelectItem value="en_cours">En cours</SelectItem>
                          <SelectItem value="termine">Terminé</SelectItem>
                          <SelectItem value="annule">Annulé</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(i)} className="h-7 w-7 p-0" data-testid={`view-intervention-${i.intervention_id}`}><Eye className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(i)} className="h-7 w-7 p-0"><Edit className="w-3.5 h-3.5" /></Button>
                      {isWebmaster && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(i.intervention_id)} className="h-7 w-7 p-0 text-red-500" data-testid={`delete-intervention-${i.intervention_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Modifier l'intervention" : "Nouvelle intervention"}</DialogTitle>
            <DialogDescription>Remplissez les détails de l'intervention</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Titre *</Label>
              <Input value={form.title} onChange={e => F("title", e.target.value)} placeholder="Ex: Soudure portail" data-testid="intervention-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => F("description", e.target.value)} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date début *</Label>
                <Input type="date" value={form.date_debut} onChange={e => F("date_debut", e.target.value)} data-testid="intervention-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Date fin</Label>
                <Input type="date" value={form.date_fin} onChange={e => F("date_fin", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Lieu</Label>
                <Input value={form.location} onChange={e => F("location", e.target.value)} placeholder="Adresse du chantier" />
              </div>
              <div className="space-y-1.5">
                <Label>Assigné à</Label>
                <Input value={form.assigned_to} onChange={e => F("assigned_to", e.target.value)} placeholder="Nom du technicien" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Bon de commande (optionnel)</Label>
              <Select value={form.commande_id || "none"} onValueChange={v => F("commande_id", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Aucune commande liée" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune</SelectItem>
                  {commandes.filter(c => c.status !== "cancelled").map(c => (
                    <SelectItem key={c.commande_id} value={c.commande_id}>{c.commande_number} - {c.client_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => F("notes", e.target.value)} rows={2} />
            </div>
            <Button onClick={handleSave} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="save-intervention-btn">
              {editId ? "Mettre à jour" : "Créer l'intervention"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog - Photos, Comments, Progress */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl uppercase tracking-tight">{selectedIntervention?.title}</DialogTitle>
            <DialogDescription>
              {selectedIntervention?.commande_number && `Commande: ${selectedIntervention.commande_number} | `}
              {selectedIntervention?.client_name}
            </DialogDescription>
          </DialogHeader>
          {selectedIntervention && (
            <div className="space-y-5">
              {/* Status & Progress */}
              <div className="flex items-center gap-4">
                <Badge className={`${statusColors[selectedIntervention.status]} border-0 px-3 py-1`}>
                  {statusLabels[selectedIntervention.status]}
                </Badge>
                <div className="flex-1">
                  <div className="flex justify-between text-xs text-slate-400 mb-1">
                    <span>Progression</span>
                    <span>{selectedIntervention.progress || 0}%</span>
                  </div>
                  <input
                    type="range" min="0" max="100" step="5"
                    value={selectedIntervention.progress || 0}
                    onChange={e => handleProgressUpdate(selectedIntervention.intervention_id, parseInt(e.target.value))}
                    className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-blue-600"
                    data-testid="progress-slider"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 p-3 rounded-md">
                  <p className="text-xs text-slate-400">Date début</p>
                  <p className="font-medium">{formatDate(selectedIntervention.date_debut)}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-md">
                  <p className="text-xs text-slate-400">Lieu</p>
                  <p className="font-medium">{selectedIntervention.location || "-"}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-md">
                  <p className="text-xs text-slate-400">Technicien</p>
                  <p className="font-medium">{selectedIntervention.assigned_to || "-"}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-md">
                  <p className="text-xs text-slate-400">Client</p>
                  <p className="font-medium">{selectedIntervention.client_name || "-"}</p>
                </div>
              </div>

              {selectedIntervention.description && (
                <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded-md">
                  <p className="text-xs text-slate-400 mb-1">Description</p>
                  {selectedIntervention.description}
                </div>
              )}

              <Separator />

              {/* Photos Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                    <Camera className="w-4 h-4" /> Photos ({selectedIntervention.photos?.length || 0})
                  </h4>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file" multiple accept="image/*"
                      className="hidden"
                      onChange={e => handlePhotoUpload(e.target.files)}
                    />
                    <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="h-7 text-xs" data-testid="upload-photo-btn">
                      <Upload className="w-3 h-3 mr-1" /> {uploading ? "Envoi..." : "Ajouter photos"}
                    </Button>
                  </div>
                </div>
                {selectedIntervention.photos?.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {selectedIntervention.photos.map((url, idx) => (
                      <a key={idx} href={`${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block">
                        <img src={`${BACKEND_URL}${url}`} alt={`Photo ${idx + 1}`} className="w-full h-24 object-cover rounded-md border border-slate-200 hover:opacity-80 transition-opacity" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 text-center py-4">Aucune photo</p>
                )}
              </div>

              <Separator />

              {/* Comments Section */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-3">
                  <MessageSquare className="w-4 h-4" /> Commentaires ({selectedIntervention.comments?.length || 0})
                </h4>
                {selectedIntervention.comments?.length > 0 && (
                  <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                    {selectedIntervention.comments.map((c, idx) => (
                      <div key={idx} className={`p-3 rounded-md text-sm ${c.visibility === "internal" ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-xs text-slate-700">{c.author}</span>
                          <div className="flex items-center gap-2">
                            <Badge className={`text-[9px] border-0 ${c.visibility === "internal" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                              {c.visibility === "internal" ? "Interne" : "Public"}
                            </Badge>
                            <span className="text-[10px] text-slate-400">{formatDate(c.created_at)}</span>
                          </div>
                        </div>
                        <p className="text-slate-600">{c.text}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add comment form */}
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Textarea
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      placeholder="Ajouter un commentaire..."
                      rows={2}
                      className="text-sm"
                      data-testid="comment-input"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Select value={commentVisibility} onValueChange={setCommentVisibility}>
                      <SelectTrigger className="h-7 text-xs w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="internal">Interne</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={handleAddComment} disabled={!commentText.trim()} className="h-8 bg-slate-900" data-testid="send-comment-btn">
                      <Send className="w-3 h-3 mr-1" /> Envoyer
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
